import actionsMap from '@/common/utils/notifications/actionsMap';
import { ComputedRef, Ref, computed, inject } from 'vue';
import { ElNotificationOptions } from '@uikit/ui-kit/types/notification';
import { NotificationType } from '@/common/constants/notifications.constants';
import { isFunction } from 'lodash-es';
import { useI18n } from '@/common/composables/vue-i18n.composable';
import ErrorDetails from '@/common/shared/dialogs/ErrorDetails.vue';
import useGlobalDialog from '@/common/composables/useGlobalDialog';
import type { Location } from 'vue-router';

export default function useNotification({
  textRowCount,
  notification,
}: {
  textRowCount: ComputedRef<number>,
  notification: Ref<ElNotificationOptions>,
}) {
  const { t } = useI18n();

  const globalConfirm = inject('globalConfirm') as () => TypeConfirm;

  const MAX_ROWS = 10;

  const showErrorDetails = (content) => {
    const globalDialog = useGlobalDialog();

    globalDialog.reveal({
      dialog: {
        title: notification.value.type === NotificationType.ERROR ? t('Error details') : t('Details'),
        widthSize: 'medium'
      },
      component: {
        name: ErrorDetails,
        attrOrProp: {
          content
        },
      },
      actions: [
        {
          label: t('Close'),
          slotName: 'footer',
          type: 'primary',
          click: () => globalDialog.cancel()
        },
      ]
    });
  };

  type ActionButton = {
    label: string;
    name: string;
    route?: Location;
    click?: () => void;
  };

  const moreThanMaxRows = computed(() => textRowCount.value > MAX_ROWS);

  const showDetailsActionButton = computed<ActionButton[]>(() => moreThanMaxRows.value ? [{
    label: t('Details'),
    name: 'error-details',
    click: () => showErrorDetails(notification.value.message)
  }] : []);

  const actionButtons = computed(() => {
    const actionsArray: ActionButton[] = notification.value.actions?.filter(action => isFunction(actionsMap[action.name]))
      .map(action => {
        const actionFn = actionsMap[action.name];
        const payload = {
          ...action.payload,
          notificationId: notification.value.id,
        };

        if (action.route) {
          return {
            label: action.label,
            name: action.name,
            route: action.route
          };
        }

        return {
          label: action.label,
          name: action.name,
          click: () => actionFn({ globalConfirm }, payload)
        };
      }) || [];

    return [...showDetailsActionButton.value, ...actionsArray];
  });

  return {
    actionButtons,
    moreThanMaxRows,
  };
}
